// Imperative dialog helper — replaces the Angular MatDialog injection pattern.
// Usage:
//   const { open } = useDialog();
//   const result = await open<MyResult>((onClose) => <MyDialog onClose={onClose} />);
//
// Each call mounts the provided element under DialogHost and resolves with whatever value
// the caller passes to `onClose`. Passing `null` means dismissed without action.
import { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { create } from 'zustand';

interface OpenDialog {
  id: number;
  render: (close: (value: unknown) => void) => ReactElement;
  resolve: (value: unknown) => void;
}

interface DialogStore {
  next: number;
  dialogs: OpenDialog[];
  push: (d: OpenDialog) => void;
  remove: (id: number) => void;
}

const useDialogStore = create<DialogStore>((set) => ({
  next: 1,
  dialogs: [],
  push: (d) => set((s) => ({ dialogs: [...s.dialogs, d] })),
  remove: (id) => set((s) => ({ dialogs: s.dialogs.filter((x) => x.id !== id) })),
}));

export function useDialog() {
  const open = useCallback(
    <T,>(render: (close: (value: T | null) => void) => ReactElement): Promise<T | null> => {
      return new Promise<T | null>((resolve) => {
        const store = useDialogStore.getState();
        const id = store.next;
        useDialogStore.setState({ next: id + 1 });
        store.push({
          id,
          resolve: resolve as (value: unknown) => void,
          render: (close) => render(close as (value: T | null) => void),
        });
      });
    },
    [],
  );
  return { open };
}

export function DialogHost({ children }: { children?: ReactNode }) {
  const dialogs = useDialogStore((s) => s.dialogs);
  const remove = useDialogStore((s) => s.remove);

  return (
    <>
      {children}
      {dialogs.map((d) => (
        <DialogShell key={d.id} dialog={d} onResolved={() => remove(d.id)} />
      ))}
    </>
  );
}

function DialogShell({
  dialog,
  onResolved,
}: {
  dialog: OpenDialog;
  onResolved: () => void;
}) {
  const [rendered, setRendered] = useState<ReactElement | null>(null);

  useEffect(() => {
    setRendered(
      dialog.render((value) => {
        dialog.resolve(value);
        onResolved();
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return rendered;
}
